import React, { useState } from "react";
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Avatar,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CloudIcon from "@mui/icons-material/Cloud";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

const SecurityCard = ({ feature }) => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 20;
    setMousePos({ x, y });
  };

  return (
    <Card
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setMousePos({ x: 0, y: 0 });
      }}
      elevation={isHovered ? 0 : 2}
      sx={{
        height: "100%",
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        transition: "all 0.3s ease",
        transform: isHovered
          ? `perspective(1000px) rotateX(${-mousePos.y}deg) rotateY(${mousePos.x}deg) translateY(-8px) scale(1.02)`
          : "perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px) scale(1)",
        boxShadow: isHovered
          ? "0 20px 40px rgba(111, 45, 189, 0.25), 0 0 20px rgba(155, 93, 229, 0.15)"
          : undefined,
        border: "1px solid",
        borderColor: isHovered ? "rgba(155, 93, 229, 0.4)" : "transparent",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Avatar
          sx={{
            width: 64,
            height: 64,
            background: isHovered
              ? "linear-gradient(135deg, #9B5DE5 0%, #6F2DBD 100%)"
              : "rgba(155, 93, 229, 0.15)",
            color: isHovered ? "white" : "primary.main",
            mb: 3,
            transition: "all 0.3s ease",
            transform: isHovered
              ? "scale(1.1) rotate(5deg)"
              : "scale(1) rotate(0deg)",
            boxShadow: isHovered
              ? "0 8px 16px rgba(155, 93, 229, 0.4)"
              : "none",
          }}
        >
          {feature.icon}
        </Avatar>

        <Typography
          variant="h6"
          sx={{ mb: 2, fontWeight: 600, color: "text.primary" }}
        >
          {feature.title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.7 }}
        >
          {feature.description}
        </Typography>
      </CardContent>
    </Card>
  );
};

const Security = () => {
  const securityFeatures = [
    {
      icon: <LockIcon sx={{ fontSize: 48 }} />,
      title: "Data Encryption",
      description:
        "End-to-end encryption for all workforce data, both in transit and at rest. AES-256 encryption standard.",
    },
    {
      icon: <AdminPanelSettingsIcon sx={{ fontSize: 48 }} />,
      title: "Role-Based Access",
      description:
        "Granular permission controls for managers, supervisors, and staff. Define exactly who can see and do what.",
    },
    {
      icon: <CloudIcon sx={{ fontSize: 48 }} />,
      title: "Cloud Infrastructure",
      description:
        "99.9% uptime SLA with automated backups and disaster recovery. Your data is always available.",
    },
    {
      icon: <VerifiedUserIcon sx={{ fontSize: 48 }} />,
      title: "Compliance Ready",
      description:
        "GDPR compliant, SOC 2 certified, and aligned with labor laws. Built for enterprise requirements.",
    },
  ];

  return (
    <Box
      id="security"
      sx={{
        py: { xs: 8, md: 12 },
        background:
          "linear-gradient(135deg, rgba(155, 93, 229, 0.06) 0%, rgba(111, 45, 189, 0.06) 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Dot grid pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage:
            "radial-gradient(circle, rgba(111, 45, 189, 0.18) 1.5px, transparent 1.5px)",
          backgroundSize: "28px 28px",
          opacity: 0.7,
          zIndex: 0,
        }}
      />

      {/* Diagonal stripe overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(155, 93, 229, 0.03) 0px, rgba(155, 93, 229, 0.03) 1px, transparent 1px, transparent 40px)",
          zIndex: 0,
        }}
      />

      {/* Floating orb — top right */}
      <Box
        sx={{
          position: "absolute",
          top: "-80px",
          right: "-80px",
          width: "380px",
          height: "380px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(155, 93, 229, 0.18) 0%, transparent 70%)",
          animation: "floatSlow 9s ease-in-out infinite",
          zIndex: 0,
        }}
      />

      {/* Floating orb — bottom left */}
      <Box
        sx={{
          position: "absolute",
          bottom: "-100px",
          left: "-60px",
          width: "450px",
          height: "450px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(111, 45, 189, 0.12) 0%, transparent 70%)",
          animation: "floatSlow 11s ease-in-out infinite",
          animationDelay: "3s",
          zIndex: 0,
        }}
      />

      {/* Reuse floatSlow keyframe (already injected by Features/Dashboard) */}
      <style>{`
        @keyframes floatSlow {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(20px, -20px); }
        }
      `}</style>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h3"
            sx={{
              mb: 2,
              fontWeight: 700,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Enterprise-Grade Security
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{
              maxWidth: 700,
              mx: "auto",
            }}
          >
            Your data is protected with industry-leading security measures
          </Typography>
        </Box>

        {/* Security Cards Grid */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {securityFeatures.map((feature, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <SecurityCard feature={feature} />
            </Grid>
          ))}
        </Grid>

        {/* Trust Badge */}
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            px: 3,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(10px)",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: "text.primary",
            }}
          >
            Trusted by 500+ businesses worldwide
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Join leading enterprises who trust aroundU Monitor for their
            workforce management
          </Typography>

          {/* Trust Indicators */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 4,
              mt: 3,
            }}
          >
            {[
              "SOC 2 Type II",
              "GDPR Compliant",
              "ISO 27001",
              "99.9% Uptime",
            ].map((badge, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <VerifiedUserIcon
                  sx={{ color: "primary.main", fontSize: 20 }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                >
                  {badge}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Additional Info */}
        <Box sx={{ textAlign: "center", mt: 6 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Have questions about our security practices?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "primary.main",
              fontWeight: 600,
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            View our Security Documentation →
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Security;
